import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiSave } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import SearchableSelect from '../../components/common/SearchableSelect';
import { today } from '../../utils/format';

export default function DebitNoteForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [form, setForm] = useState({ supplier_id: '', party_id: '', date: today(), amount: '', reason: '' });
  const [suppliers, setSuppliers] = useState([]);
  const [parties, setParties] = useState([]);
  const [nextNo, setNextNo] = useState('');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    api.suppliers.list({ per_page: 500, is_active: 1 }).then(({ data }) => {
      const list = data.data || data.suppliers?.data || data.suppliers || [];
      setSuppliers(Array.isArray(list) ? list.map((s) => ({ value: s.id, label: s.name })) : []);
    }).catch(() => {});

    api.parties.list({ per_page: 500, is_active: 1 }).then(({ data }) => {
      const list = data.data || data.parties?.data || data.parties || [];
      setParties(Array.isArray(list) ? list.map((p) => ({ value: p.id, label: p.name })) : []);
    }).catch(() => {});

    if (!isEdit) {
      api.debitNotes.nextNo().then(({ data }) => setNextNo(data.note_no)).catch(() => {});
    }
  }, [isEdit]);

  useEffect(() => {
    if (isEdit) {
      api.debitNotes.get(id).then(({ data }) => {
        const n = data.debit_note;
        setForm({
          supplier_id: n.supplier_id || '',
          party_id: n.party_id || '',
          date: n.date,
          amount: n.amount,
          reason: n.reason || '',
        });
      }).catch((err) => showError('Failed to load', extractError(err)));
    }
  }, [id, isEdit]);

  const validate = () => {
    const errs = {};
    if (!form.supplier_id && !form.party_id) errs.supplier_id = 'Select a supplier or party';
    if (!form.date) errs.date = 'Date is required';
    if (!form.amount || Number(form.amount) <= 0) errs.amount = 'Enter amount greater than 0';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        supplier_id: form.supplier_id ? Number(form.supplier_id) : null,
        party_id: form.party_id ? Number(form.party_id) : null,
        amount: Number(form.amount),
      };
      if (isEdit) {
        await api.debitNotes.update(id, payload);
        toast('success', '', 'Debit note updated');
      } else {
        await api.debitNotes.create(payload);
        toast('success', '', 'Debit note created and posted');
      }
      navigate('/debit-notes');
    } catch (err) {
      showError('Failed to save', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page">
      <PageHeader
        title={isEdit ? 'Edit Debit Note' : 'New Debit Note'}
        crumbs={[{ label: 'Finance', to: '/invoices' }, { label: 'Debit Notes', to: '/debit-notes' }, { label: isEdit ? 'Edit' : 'New' }]}
      />

      <div className="card">
        <div className="card-body">
          {!isEdit && nextNo && (
            <div style={{ marginBottom: 14, fontSize: 14 }}>
              Next Note No: <b style={{ color: 'var(--primary)' }}>{nextNo}</b>
            </div>
          )}

          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Supplier</label>
              <SearchableSelect
                options={suppliers}
                value={form.supplier_id}
                onChange={(v) => { setForm({ ...form, supplier_id: v, party_id: '' }); if (errors.supplier_id) setErrors((p) => ({ ...p, supplier_id: '' })); }}
                isInvalid={!!errors.supplier_id}
                placeholder="-- Select supplier (purchase return) --"
              />
              {errors.supplier_id && <div className="invalid-feedback">{errors.supplier_id}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Customer / Party</label>
              <SearchableSelect
                options={parties}
                value={form.party_id}
                onChange={(v) => { setForm({ ...form, party_id: v, supplier_id: '' }); }}
                placeholder="-- Select party (sales debit) --"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Date <span className="req">*</span></label>
              <input
                className={`form-control ${errors.date ? 'is-invalid' : ''}`}
                type="date"
                value={form.date}
                onChange={(e) => { setForm({ ...form, date: e.target.value }); if (errors.date) setErrors((p) => ({ ...p, date: '' })); }}
              />
              {errors.date && <div className="invalid-feedback">{errors.date}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Amount <span className="req">*</span></label>
              <input
                className={`form-control ${errors.amount ? 'is-invalid' : ''}`}
                type="number"
                step="0.01"
                value={form.amount}
                onChange={(e) => { setForm({ ...form, amount: e.target.value }); if (errors.amount) setErrors((p) => ({ ...p, amount: '' })); }}
              />
              {errors.amount && <div className="invalid-feedback">{errors.amount}</div>}
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Reason / Description</label>
              <textarea
                className="form-control"
                rows={2}
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="e.g. goods returned, quality issue, rate difference..."
              />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
            <button className="btn btn-secondary" onClick={() => navigate('/debit-notes')}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              <FiSave /> {saving ? 'Saving...' : isEdit ? 'Update' : 'Create & Post'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
